<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard">
        <template v-slot:header>Edit campaign</template>
        <template v-slot:subheader>{{ props.campaignId }}</template>

        <div class="flex flex-col gap-4">
            <vx-box color="info" class="p-4 text-sm" data-test="fixed-fields">
                <div class="font-bold mb-2">Fixed after creation</div>
                <dl class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div>
                        <dt class="font-bold">Brand</dt>
                        <dd class="font-mono text-slate-500">{{ props.campaign.brand_id }}</dd>
                    </div>
                    <div>
                        <dt class="font-bold">Use case</dt>
                        <dd class="text-slate-500">{{ props.campaign.usecase }}</dd>
                    </div>
                    <div>
                        <dt class="font-bold">Sub use cases</dt>
                        <dd class="text-slate-500">{{ props.campaign.sub_usecases.join(', ') || '—' }}</dd>
                    </div>
                    <div>
                        <dt class="font-bold">Terms accepted</dt>
                        <dd class="text-slate-500">{{ yesNo(props.campaign.terms_and_conditions) }}</dd>
                    </div>
                    <div data-test="subscriber-flags">
                        <dt class="font-bold">Subscriber flags</dt>
                        <dd class="text-slate-500">
                            Opt-in {{ yesNo(props.campaign.subscriber_optin) }} · Opt-out
                            {{ yesNo(props.campaign.subscriber_optout) }} · Help
                            {{ yesNo(props.campaign.subscriber_help) }}
                        </dd>
                    </div>
                </dl>
            </vx-box>

            <kiosk-tcr-campaign-packet-fields
                v-model="packet"
                :externalResults="packetExternalResults"
                :minSamples="1"
                editing
            />
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    data-test="cancel-packet"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="save()"
                    data-test="save-packet"
                    :loading="saving"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Save
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxBox, VxButton, VxModal } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { computed, ref, watch } from 'vue';
import KioskTcrCampaignPacketFields from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrCampaignPacketFields.vue';
import { campaignDefaults, packetKeys } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationFormDefaults';
import { yesNo } from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrCampaignPacket, TcrLiveCampaign } from '~/services/adminTenDlcRegistrationClient.types';
import { nestLaravelErrors, validateRules } from '~/utils/validation';

const props = defineProps<{ campaignId: string; campaign: TcrLiveCampaign }>();
const emit = defineEmits<{ updated: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();

// The live record is nullable field by field; vx-toggle and vx-input never receive null.
const seedPacket = (campaign: TcrLiveCampaign): TcrCampaignPacket => {
    const defaults = campaignDefaults();

    return {
        ...(Object.fromEntries(
            packetKeys.map((key) => [key, campaign[key] ?? defaults[key]])
        ) as unknown as TcrCampaignPacket),
        samples: [...(campaign.samples ?? defaults.samples)],
    };
};

const packet = ref<TcrCampaignPacket>(seedPacket(props.campaign));
const packetExternalResults = computed(() => nestLaravelErrors($externalResults.value));
// Owns the external results so validateRules() clears them for the packet fields before a resubmit.
const validate = useVuelidate({ $externalResults });
const saving = ref(false);

watch(visible, (open) => {
    if (open) {
        packet.value = seedPacket(props.campaign);
        validate.value.$reset();
    }
});

const save = async () => {
    if (saving.value) {
        return;
    }

    saving.value = true;
    validate.value.$reset();

    if (!(await validateRules(validate))) {
        saving.value = false;
        return;
    }

    try {
        await adminTenDlcRegistrationClient.updateCampaign(props.campaignId, packet.value);

        toasted.success('Campaign updated.');
        emit('updated');
        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        saving.value = false;
    }
};
</script>
